import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import EmptyState from '../../components/EmptyState';
import InlineAlert from '../../components/InlineAlert';
import { useBilling } from '../../context/useBilling';

const toDisplayDate = (isoDate) => {
  if (!isoDate) return '—';
  const parsed = new Date(isoDate);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleDateString();
};

const toDisplayAmount = (amount) => `$${Number(amount || 0).toFixed(2)}`;

const getBillMonthForDisplay = (bill) => {
  if (bill?.billMonth) return bill.billMonth;
  if (bill?.period?.start) return bill.period.start.slice(0, 7);
  return null;
};

const toDisplayMonth = (billMonth) => {
  if (!billMonth) return '—';
  const parsed = new Date(`${billMonth}-01T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return billMonth;
  return parsed.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
};

const toNumeric = (value) => Number(value || 0);

const BillingHistory = () => {
  const navigate = useNavigate();
  const { account, billingHistory, paymentResult, currentBill, loadBillingHistory, loading, error } = useBilling();

  useEffect(() => {
    if (account && billingHistory.length === 0 && !loading.history) {
      loadBillingHistory();
    }
  }, [account, billingHistory.length, loadBillingHistory, loading.history]);

  if (!account) {
    return (
      <div className="row billing-panel" role="tabpanel" id="billing-panel-billing-history" aria-labelledby="billing-tab-billing-history" tabIndex={0} data-testid="billing-panel-billing-history">
        <div className="col-12">
          <div className="card border-0 shadow-sm billing-surface-card billing-surface-card--feature">
            <div className="card-header bg-light border-0">
              <h5 className="card-title mb-0">
                <i className="bi bi-clock-history me-2 text-warning"></i>
                Billing History
              </h5>
            </div>
            <div className="card-body">
              <InlineAlert
                variant="info"
                icon="info-circle"
                title="Billing History"
                message="View detailed billing history and payment records for selected customer accounts."
                className="border-0 mb-4"
              />
              <EmptyState
                icon="clock-history"
                eyebrow="Billing History"
                title="No Customer Selected"
                message="Please search for and select a customer account to view billing history."
                compact
                actions={(
                  <button
                    className="btn btn-outline-tse-primary"
                    onClick={() => navigate('../customer-lookup')}
                  >
                    <i className="bi bi-search me-2"></i>Find Customer
                  </button>
                )}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading.history && billingHistory.length === 0) {
    return (
      <div className="d-flex align-items-center gap-2" data-testid="billing-history-loading">
        <span className="spinner-border" role="status" aria-hidden="true"></span>
        <span>Loading billing history...</span>
      </div>
    );
  }

  if (error && billingHistory.length === 0) {
    return <InlineAlert variant="danger" icon="exclamation-octagon" ariaLabel="Billing history error" message={error} />;
  }

  const totalBilled = billingHistory.reduce((sum, bill) => sum + toNumeric(bill.amountDue), 0);
  const averageMonthly = billingHistory.length > 0 ? totalBilled / billingHistory.length : 0;
  const latestPaidAt = billingHistory.length > 0 ? toDisplayDate(billingHistory[0].paidAt) : '—';

  return (
    <div className="row billing-panel" role="tabpanel" id="billing-panel-billing-history" aria-labelledby="billing-tab-billing-history" tabIndex={0} data-testid="billing-panel-billing-history">
      <div className="col-12">
        <div className="card border-0 shadow-sm billing-surface-card billing-surface-card--feature">
          <div className="card-header bg-light border-0">
            <h5 className="card-title mb-0">
              <i className="bi bi-clock-history me-2 text-warning"></i>
              Billing History
            </h5>
          </div>
          <div className="card-body">
            <InlineAlert
              variant="info"
              icon="info-circle"
              title="Billing History Overview"
              message={`Showing the previous 11 monthly bills for account ${account.accountNumber}.`}
              className="border-0 mb-4"
            />

            <div className="row g-3 mb-4">
              <div className="col-md-3">
                <div className="card h-100 bg-light border-0 billing-kpi-card">
                  <div className="card-body py-3">
                    <div className="small text-muted mb-1">Bills Displayed</div>
                    <div className="h5 mb-0 fw-bold">{billingHistory.length}</div>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card h-100 bg-light border-0 billing-kpi-card">
                  <div className="card-body py-3">
                    <div className="small text-muted mb-1">Total Billed</div>
                    <div className="h5 mb-0 fw-bold">{toDisplayAmount(totalBilled)}</div>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card h-100 bg-light border-0 billing-kpi-card">
                  <div className="card-body py-3">
                    <div className="small text-muted mb-1">Average Monthly</div>
                    <div className="h5 mb-0 fw-bold">{toDisplayAmount(averageMonthly)}</div>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card h-100 bg-light border-0 billing-kpi-card">
                  <div className="card-body py-3">
                    <div className="small text-muted mb-1">Latest Payment Date</div>
                    <div className="h6 mb-0 fw-bold">{latestPaidAt}</div>
                  </div>
                </div>
              </div>
            </div>

            {billingHistory.length === 0 && (
              <InlineAlert
                variant="secondary"
                icon="clock-history"
                message="No billing history is available for this account yet."
                className="mb-4"
              />
            )}

            <div className="table-responsive billing-table-wrap" role="region" aria-label="Billing history for previous 11 months">
              <table className="table table-sm table-hover align-middle billing-table billing-history-table" data-testid="table-billing-history">
                <caption className="visually-hidden">Billing history for previous 11 months</caption>
                <thead className="table-light">
                  <tr>
                    <th scope="col" className="text-nowrap">Confirmation #</th>
                    <th scope="col">Bill Month</th>
                    <th scope="col">Period</th>
                    <th scope="col" className="ps-4 pe-2 text-nowrap">Due Date</th>
                    <th scope="col" className="text-end ps-2 pe-4 text-nowrap">Amount Due</th>
                    <th scope="col" className="text-end px-4 text-nowrap">Amount Paid</th>
                    <th scope="col" className="px-4 text-nowrap">Paid Date</th>
                    <th scope="col">Status</th>
                    <th scope="col" className="text-center text-nowrap">Download</th>
                  </tr>
                </thead>
                <tbody>
                  {paymentResult && (
                    <tr className="table-success" data-testid="row-recent-payment">
                      <td className="fw-semibold text-nowrap billing-mono">{paymentResult.confirmationCode}</td>
                      <td>
                        <div className="fw-semibold">{toDisplayMonth(getBillMonthForDisplay(currentBill))}</div>
                        <span className="badge bg-success bg-opacity-25 text-success">Just Now</span>
                      </td>
                      <td>
                        {currentBill?.period ? (
                          <>
                            {toDisplayDate(currentBill.period.start)} - {toDisplayDate(currentBill.period.end)}
                            <div className="small text-muted">{currentBill.period.days || 0} days</div>
                          </>
                        ) : '—'}
                      </td>
                      <td className="ps-4 pe-2 text-nowrap">{currentBill?.period ? toDisplayDate(currentBill.period.dueDate) : '—'}</td>
                      <td className="text-end ps-2 pe-4 fw-semibold text-nowrap">${Number(paymentResult.amount).toFixed(2)}</td>
                      <td className="text-end px-4 text-nowrap">${Number(paymentResult.amount).toFixed(2)}</td>
                      <td className="px-4 text-nowrap">{new Date(paymentResult.processedAt).toLocaleDateString()}</td>
                      <td><span className="badge bg-success"><i className="bi bi-check-circle me-1"></i>PAID</span></td>
                      <td className="text-center">
                        <button
                          type="button"
                          className="btn btn-link p-0 text-danger pdf-download-placeholder"
                          title="Download PDF (coming soon)"
                          aria-label={`Download payment receipt ${paymentResult.confirmationCode} (coming soon)`}
                          onClick={(e) => e.preventDefault()}
                        >
                          <i className="bi bi-file-earmark-pdf-fill fs-5"></i>
                        </button>
                      </td>
                    </tr>
                  )}
                  {billingHistory.map((bill) => (
                    <tr key={bill.billMonth}>
                      <td className="text-nowrap billing-mono">{bill.confirmationCode || '—'}</td>
                      <td>
                        <div className="fw-semibold">{toDisplayMonth(bill.billMonth)}</div>
                        <div className="small text-muted">{bill.billMonth}</div>
                      </td>
                      <td>
                        {toDisplayDate(bill.period?.start)} - {toDisplayDate(bill.period?.end)}
                        <div className="small text-muted">{bill.period?.days || 0} days</div>
                      </td>
                      <td className="ps-4 pe-2 text-nowrap">{toDisplayDate(bill.period?.dueDate)}</td>
                      <td className="text-end ps-2 pe-4 fw-semibold text-nowrap">{toDisplayAmount(bill.amountDue)}</td>
                      <td className="text-end px-4 text-nowrap">{toDisplayAmount(bill.amountPaid)}</td>
                      <td className="px-4 text-nowrap">{toDisplayDate(bill.paidAt)}</td>
                      <td><span className="badge bg-success">{bill.status || 'PAID'}</span></td>
                      <td className="text-center">
                        <button
                          type="button"
                          className="btn btn-link p-0 text-danger pdf-download-placeholder"
                          title="Download PDF (coming soon)"
                          aria-label={`Download PDF for ${bill.billMonth} (coming soon)`}
                          onClick={(e) => e.preventDefault()}
                        >
                          <i className="bi bi-file-earmark-pdf-fill fs-5"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillingHistory;
